"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.arrayType = void 0;
const actual_type_1 = require("./actual-type");
const caster_1 = require("../caster");
const caster_cache_1 = require("./caster-cache");
const ramda_1 = require("ramda");
function arrayType(declaration, validateOptions, circularCache = caster_cache_1.createCache()) {
    if (declaration === null || typeof declaration !== 'object') {
        throw new Error('First argument (declaration) must be an Object.');
    }
    const circularCaster = circularCache.get(declaration);
    if (circularCaster !== undefined) {
        return circularCaster;
    }
    const { type, required, uniqueItems, removeDuplicate, items, minItems, maxItems, empty } = declaration;
    if (type !== 'Array') {
        throw new Error('"type" property on declaration must be "Array"');
    }
    if (required !== undefined && typeof required !== 'boolean') {
        throw new Error('"required" property on declaration must be undefined or a Boolean');
    }
    const defaultCandidate = Reflect.get(declaration, 'default');
    if (defaultCandidate !== undefined && (Array.isArray(defaultCandidate) === false || defaultCandidate.length !== 0)) {
        throw new Error('"default" property on declaration must be undefined or an empty Array');
    }
    if (uniqueItems !== undefined && typeof uniqueItems !== 'boolean') {
        throw new Error('"uniqueItems" property on declaration must be undefined or a Boolean');
    }
    if (removeDuplicate !== undefined && typeof removeDuplicate !== 'boolean') {
        throw new Error('"removeDuplicate" property on declaration must be undefined or a Boolean');
    }
    if (typeof items !== 'object' || items === null) {
        throw new Error('"items" property on declaration must be an Object');
    }
    let caster = (candidate) => ({ type, isValid: true, invalidations: [], candidate });
    if (maxItems !== undefined) {
        if (typeof maxItems !== 'number') {
            throw new Error('"maxItems" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (maxItems < candidate.length) {
                return {
                    ...result,
                    invalidations: [[[], 'Array', 'maxItems', maxItems, candidate.length], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (minItems !== undefined) {
        if (typeof minItems !== 'number') {
            throw new Error('"minItems" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (minItems > candidate.length) {
                return {
                    ...result,
                    invalidations: [[[], 'Array', 'minItems', minItems, candidate.length], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (empty !== undefined) {
        if (typeof empty !== 'boolean') {
            throw new Error('"empty" property on declaration must undefined or a Boolean');
        }
        if (empty === false) {
            const casterRef = caster;
            caster = (candidate) => {
                const result = casterRef(candidate);
                if (candidate.length === 0) {
                    return {
                        ...result,
                        invalidations: [[[], 'Array', 'empty', empty, candidate], ...result.invalidations]
                    };
                }
                return result;
            };
        }
    }
    let itemsCaster;
    const casterRef = (candidate, register = new Map()) => {
        const originalCandidate = candidate;
        if ((candidate === undefined || candidate === null) && defaultCandidate !== undefined) {
            candidate = defaultCandidate;
        }
        let declarationRegister = register.get(declaration);
        if (declarationRegister === undefined) {
            declarationRegister = new Set();
            register.set(declaration, declarationRegister);
        }
        else if (declarationRegister.has(candidate)) {
            return {
                type,
                isValid: true,
                invalidations: [],
                candidate
            };
        }
        if (candidate === undefined || candidate === null) {
            if (required === false) {
                return {
                    type,
                    isValid: true,
                    invalidations: [],
                    undefined
                };
            }
            else {
                return {
                    type,
                    isValid: false,
                    candidate,
                    invalidations: [[[], 'Array', 'required', true, candidate]]
                };
            }
        }
        const candidateType = actual_type_1.ctorName(candidate);
        if (candidateType !== type) {
            return {
                type,
                isValid: false,
                candidate,
                invalidations: [[[], 'Array', 'type', type, candidateType]]
            };
        }
        declarationRegister.add(candidate);
        if (removeDuplicate === true) {
            candidate = candidate.filter((item, index, array) => {
                const nextItems = array.slice(index + 1);
                if (nextItems.length === 0) {
                    return true;
                }
                return nextItems.every((nextItem) => ramda_1.equals(item, nextItem) === false);
            });
        }
        if (uniqueItems === true) {
            const onlyUniqueItems = candidate.every((item, index, array) => {
                const nextItems = array.slice(index + 1);
                if (nextItems.length === 0) {
                    return true;
                }
                return nextItems.every((nextItem) => ramda_1.equals(item, nextItem) === false);
            });
            if (onlyUniqueItems === false) {
                return {
                    type,
                    isValid: false,
                    candidate,
                    invalidations: [[[], 'Array', 'uniqueItems', uniqueItems, candidate]]
                };
            }
        }
        let { invalidations } = caster(candidate);
        const castedItems = candidate.map((item, index) => {
            const itemResult = itemsCaster(item, register);
            if (itemResult.isValid === false) {
                invalidations = [
                    ...invalidations,
                    ...itemResult.invalidations.map(([path, ...rest]) => [[index, ...path], ...rest])
                ];
            }
            return itemResult.candidate;
        });
        return {
            type,
            isValid: invalidations.length === 0,
            invalidations,
            candidate: invalidations.length === 0 ? castedItems : originalCandidate
        };
    };
    circularCache.set(declaration, casterRef);
    itemsCaster = items === undefined ? undefined : caster_1.createCaster(items, validateOptions, circularCache);
    return casterRef;
}
exports.arrayType = arrayType;
